import { useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import pick from 'lodash/pick'
import debounce from 'lodash/debounce'

import { SearchQueryResult } from '../../../../hooks/useQueries'
import { updateFilters } from '../../../../actions/searchActions'

import CloseIcon from '../../../../components/icons/AirbnbClose'

import { FilterPanel, CloseButton, PanelHeader } from '../shared'
import { BOOKING_OPTIONS_FILTERS } from '../../common/BookingOptionsFilter'

import CommonMoreFilters from '../../common/MoreFilters'

import FilterPanelActions from '../FilterPanelActions'

const clearedFilters: GQL_SearchFilters = {
  regions: null,
  driveTime: null,
  priceStart: null,
  priceEnd: null,
  subcategories: null,
  amenities: null,
  petFriendly: null,
  pets: null,
  bedrooms: null,
  beds: null,
}
BOOKING_OPTIONS_FILTERS.forEach((key) => {
  clearedFilters[key] = null
})
Object.freeze(clearedFilters)

const selectFilters = (filters: Filters) => {
  return {
    ...clearedFilters,
    ...pick(filters, [
      'regions',
      'driveTime',
      'priceStart',
      'priceEnd',
      'subcategories',
      'amenities',
      'petFriendly',
      'pets',
      'bedrooms',
      'beds',
      ...BOOKING_OPTIONS_FILTERS,
    ]),
  }
}

const usePriceBounds = (
  panelOpen: boolean,
  minPrice: number,
  maxPrice: number,
) => {
  const [priceBounds, setPriceBounds] = useState<[number, number]>()

  // update when panel is opened or closed
  useEffect(() => {
    setPriceBounds([minPrice, maxPrice])
  }, [panelOpen])

  // If has remained open then ignore the change in min/max price unless this is the first set of results
  // with a min/max value. This prevents the price slider range shifting with every drag of the price slider.
  useEffect(() => {
    if (priceBounds?.[0] == null || priceBounds?.[1] == null) {
      setPriceBounds([minPrice, maxPrice])
    }
  }, [minPrice, maxPrice])

  return priceBounds
}

type Filters = Omit<GQL_SearchFilters, 'state'> & {
  state?: string
}

const useFiltersState = (filters: Filters) => {
  const filtersHaveUpdatedOnce = useRef(false)
  const [state, setState] = useState(selectFilters(filters))

  useEffect(() => {
    if (filtersHaveUpdatedOnce.current === false) {
      setState(selectFilters(filters))
      filtersHaveUpdatedOnce.current = true
    }
  }, [filters])

  return [state, setState] as const
}

type MoreFiltersProps = {
  open: boolean
  filters: Filters
  defaults: SearchQueryResult['data']['search']['defaults']
  minPrice: number
  maxPrice: number
  update: (_filters: Filters) => void
  onClose: () => any
  includeRegionsFilter?: boolean
  resultsCount?: number
  updateDebounceMs?: number
}

const MoreFilters = ({
  filters,
  open,
  defaults,
  includeRegionsFilter,
  minPrice,
  maxPrice,
  onClose,
  update,
  resultsCount,
  updateDebounceMs = 500,
}: MoreFiltersProps) => {
  const [showResultCount, setShowResultCount] = useState(true)
  const debouncedUpdate = useMemo(
    () =>
      debounce((nextFilters: Filters) => {
        update(nextFilters)
        setShowResultCount(true)
      }, updateDebounceMs),
    [update],
  )

  const scrollContainer = useRef<HTMLDivElement>()

  const [state, setState] = useFiltersState(filters)

  const priceBounds = usePriceBounds(open, minPrice, maxPrice)

  const handleFiltersChange = (changes: Partial<Filters>) => {
    const nextFilters = {
      ...state,
      ...changes,
    }
    setState(nextFilters)
    setShowResultCount(false)
    debouncedUpdate(nextFilters)
  }

  const handlePriceRangeChange = ([priceStart, priceEnd]: [number, number]) => {
    const nextFilters = {
      ...state,
      priceStart: priceStart >= minPrice ? priceStart : null,
      priceEnd: priceEnd <= maxPrice ? priceEnd : null,
    }
    handleFiltersChange(nextFilters)
  }

  const resetScroll = () => {
    scrollContainer.current.scrollTo(0, 0)
  }

  const handleClosePanel = () => {
    resetScroll()
    onClose()
  }

  const handleApply = () => {
    handleClosePanel()
  }

  const handleResetFilters = () => {
    const nextFilters = {
      ...filters,
      ...clearedFilters,
    }
    debouncedUpdate(nextFilters)
    setState({ ...clearedFilters })
    onClose()
  }

  const {
    petFriendly,
    regions,
    driveTime,
    priceStart,
    priceEnd,
    subcategories,
    amenities,
    bedrooms,
    beds,
    fullRefund,
    instantBook,
    oneNightStayWeekday,
    oneNightStayWeekend,
  } = selectFilters(state)

  return (
    <FilterPanel className={`${open ? 'show' : ''}`} ref={scrollContainer}>
      <PanelHeader>
        <CloseButton
          data-testid="close-more-filters"
          aria-label="Close additional filters"
          onClick={handleClosePanel}
        >
          <CloseIcon />
        </CloseButton>
        <span>Filters</span>
      </PanelHeader>

      <CommonMoreFilters
        defaults={defaults}
        appliedFilters={filters}
        filterValuesState={{
          petFriendly,
          regions,
          driveTime,
          subcategories,
          amenities,
          priceBounds,
          priceStart,
          priceEnd,
          bedrooms,
          beds,
          fullRefund,
          instantBook,
          oneNightStayWeekday,
          oneNightStayWeekend,
        }}
        includeRegionsFilter={includeRegionsFilter}
        handleFiltersChange={handleFiltersChange}
        handlePriceRangeChange={handlePriceRangeChange}
      />

      <FilterPanelActions
        onClear={handleResetFilters}
        onView={handleApply}
        clearLabel="Clear all"
        resultsCount={!showResultCount ? undefined : resultsCount}
      />
    </FilterPanel>
  )
}

export { MoreFilters }

export default connect(undefined, (dispatch) => ({
  update: (filters: Filters) =>
    dispatch(
      // @ts-ignore -- not typed yet
      updateFilters(filters),
    ),
}))(MoreFilters)
